import React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { DataTable, type DataTableColumn, type FilterPill, type SortState } from '@/components/shared/DataTable';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Checkbox as UntypedCheckbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader,
  DialogFooter, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useOrganisations } from '@/hooks/queries/useAdmin';
import { useCreateOrganisation } from '@/hooks/mutations/useAdminMutations';
import { useQueryClient } from '@tanstack/react-query';
import { OrganisationRowMenu, ErrorLine, confirmNameOf, statusOf } from '@/components/backoffice/organisations/OrganisationRowMenu';
import { purgeConfirmPhrase, purgeOrganisations } from '@/api/endpoints/organisationLifecycle';
import { useLifecycleError } from '@/components/backoffice/organisations/lifecycleError';
import { toast } from 'sonner';
import type { OrganisationRow } from '@/api/endpoints/admin';
import { showErrorToast, toUserMessage } from '@/lib/errorToast';
import { ApiErrorException } from '@/api/client';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/stores/authStore';
import PageHeader from '@/components/vf/PageHeader';
import { openOrganisationTeam } from '@/lib/openOrganisationTeam';
import { emailFieldError } from '@/pages/auth/AuthCard';

/**
 * /AdminOrganisations — super-admin-only listing + provisioning of
 * Organisations. Mirrors the backend route /api/admin/organisations
 * (super_admin guard enforced server-side; RequireRole guards the UI).
 *
 * W6 (B3-AdminOrganisations, DES-11): the organisations table first, then
 * the operational exceptions. The table: search, status filter, usage
 * columns (members, active numbers, calls over 30 days, last call;
 * « Indisponible » when the server could not count, DES-19), pages of 25
 * (T-h4), and a menu per row: open the team (the organisation becomes the
 * active one, DUP-3), its audit log, copy its id. Creation invites the first
 * admin by email: the super admin never types a password for someone else.
 *
 * SU-1 (W7): the row menu (OrganisationRowMenu) edits, deactivates,
 * reactivates, soft-deletes (typed name), « ouvre en tant que », lists and
 * adds members, sends the welcome notification, and holds the two UI flag
 * switches (SR2-7, they were columns). Rows can be selected for a permanent
 * purge (bulk-delete-clients) behind a typed confirmation (SR1-1.6, ENG-17).
 * The default status filter leaves soft-deleted organisations out.
 */

// components/ui/checkbox is untyped JS (as in AdminNotifications).
const Checkbox = UntypedCheckbox as unknown as (props: { checked: boolean; onCheckedChange: (v: boolean | 'indeterminate') => void; 'aria-label'?: string }) => JSX.Element;

const PAGE_SIZE = 25;
type StatusFilter = 'all' | 'active' | 'inactive' | 'deleted';
type Exception = 'inactive' | 'noMembers' | 'noCalls' | 'noNumbers';
const EXCEPTIONS: Exception[] = ['noMembers', 'noCalls', 'noNumbers', 'inactive'];

/** Usage can be missing (older API) or null (could not be counted): both read « Indisponible ». */
const usageOf = (o: OrganisationRow) => o.usage ?? { members: null, numbers: null, calls_30d: null, last_call_at: null };

function isException(o: OrganisationRow, kind: Exception): boolean {
  const u = usageOf(o);
  switch (kind) {
    case 'inactive': return statusOf(o) === 'disabled';
    case 'noMembers': return u.members === 0;
    case 'noCalls': return o.is_active && u.calls_30d === 0;
    case 'noNumbers': return o.is_active && u.numbers === 0;
  }
}

export default function AdminOrganisations() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();
  const { canCreateOrganisation } = usePermissions();
  const activeOrganisationId = useAuthStore((s) => s.activeMembership?.organisationId);
  const switchOrganisation = useAuthStore((s) => s.switchOrganisation);
  const { data, isLoading, error, refetch } = useOrganisations();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [exception, setException] = useState<Exception | null>(null);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<SortState | null>({ id: 'company', dir: 'asc' });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeError, setPurgeError] = useState<unknown>(null);
  // A new dialog per attempt: Réessayer starts from an empty confirmation field.
  const [purgeAttempt, setPurgeAttempt] = useState(0);
  const qc = useQueryClient();
  const needle = query.trim().toLowerCase();
  const all = useMemo(() => data?.organisations ?? [], [data]);

  // Search, filters and sort run here: the list is fully loaded (500 at most,
  // a cap the « partial » line states).
  const rows = useMemo(() => {
    const found = all.filter((o) => (
      (!needle || [o.company_name, o.email, o.contact_name].some((v) => (v ?? '').toLowerCase().includes(needle)))
      && (status === 'all' ? statusOf(o) !== 'deleted' : statusOf(o) === ({ active: 'active', inactive: 'disabled', deleted: 'deleted' } as const)[status])
      && (!exception || isException(o, exception))
    ));
    if (!sort) return found;
    const num = (v: number | null | undefined) => (typeof v === 'number' ? v : -1);
    const key = (o: OrganisationRow): string | number => {
      switch (sort.id) {
        case 'status': return ['active', 'disabled', 'deleted'].indexOf(statusOf(o));
        case 'members': return num(usageOf(o).members);
        case 'calls': return num(usageOf(o).calls_30d);
        case 'lastCall': return usageOf(o).last_call_at ?? '';
        default: return (o.company_name ?? '').toLocaleLowerCase();
      }
    };
    const sorted = [...found].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      return typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb));
    });
    return sort.dir === 'desc' ? sorted.reverse() : sorted;
  }, [all, needle, status, exception, sort]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const openTeam = (org: OrganisationRow) => {
    openOrganisationTeam(org.id, { activeOrganisationId, switchOrganisation, navigate }).catch((err) => showErrorToast(err));
  };

  const unavailable = <span className="text-muted-foreground">{t('admin:organisations.unavailable')}</span>;
  const count = (v: number | null | undefined) => (typeof v === 'number' ? <span className="tabular-nums">{v.toLocaleString(i18n.language)}</span> : unavailable);

  const columns: DataTableColumn<OrganisationRow>[] = [
    {
      id: 'company', header: t('admin:organisations.columns.company'), sortable: true,
      cell: (org) => (
        <span className="flex items-center gap-3">
          <Checkbox
            checked={selected.has(org.id)}
            onCheckedChange={(v: boolean | 'indeterminate') => toggle(org.id, v === true)}
            aria-label={t('admin:organisations.lifecycle.select', { company: org.company_name })}
          />
          <button type="button" className="min-h-11 text-left font-medium hover:underline disabled:no-underline disabled:text-muted-foreground" disabled={statusOf(org) !== 'active'} onClick={() => openTeam(org)} title={t('admin:organisations.actions.openTeam')}>
            {org.company_name}
          </button>
        </span>
      ),
    },
    { id: 'email', header: 'Email', hideable: true, defaultHidden: true, cell: (org) => org.email },
    { id: 'contact', header: t('admin:organisations.columns.contact'), hideable: true, defaultHidden: true, cell: (org) => org.contact_name },
    {
      id: 'status', header: t('admin:organisations.columns.status'), sortable: true,
      cell: (org) => {
        const st = statusOf(org);
        if (st === 'active') return <Badge variant="default">{t('common:states.active')}</Badge>;
        if (st === 'deleted') return <Badge variant="outline" className="border-destructive text-destructive">{t('admin:organisations.lifecycle.statusDeleted')}</Badge>;
        return <Badge variant="secondary">{t('admin:organisations.lifecycle.statusDisabled')}</Badge>;
      },
    },
    { id: 'members', header: t('admin:organisations.columns.members'), sortable: true, hideable: true, align: 'right', cell: (org) => count(usageOf(org).members) },
    { id: 'numbers', header: t('admin:organisations.columns.numbers'), hideable: true, align: 'right', cell: (org) => count(usageOf(org).numbers) },
    { id: 'calls', header: t('admin:organisations.columns.calls30d'), sortable: true, hideable: true, align: 'right', cell: (org) => count(usageOf(org).calls_30d) },
    {
      id: 'lastCall', header: t('admin:organisations.columns.lastCall'), sortable: true, hideable: true,
      cell: (org) => {
        const u = usageOf(org);
        if (u.calls_30d === null) return unavailable;
        return u.last_call_at
          ? new Date(u.last_call_at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })
          : <span className="text-muted-foreground" title={t('admin:organisations.noCallIn30d')}>—</span>;
      },
    },
    {
      // QA-W7-12: at 390 px the menu stays on screen while the table scrolls.
      id: 'actions', header: <span className="sr-only">{t('admin:organisations.columns.actions')}</span>, align: 'right', sticky: 'right',
      cell: (org) => (
        <OrganisationRowMenu
          org={org}
          onOpenTeam={() => openTeam(org)}
          onAuditLog={() => navigate(`/AdminAuditLogs?organisation_id=${encodeURIComponent(org.id)}`)}
        />
      ),
    },
  ];

  const resetPage = () => setPage(0);
  const filters: FilterPill[] = [
    ...(needle ? [{ id: 'q', label: `« ${query.trim()} »`, onRemove: () => { setQuery(''); resetPage(); } }] : []),
    ...(status !== 'all' ? [{ id: 'status', label: t(`admin:organisations.status.${status}`), onRemove: () => { setStatus('all'); resetPage(); } }] : []),
    ...(exception ? [{ id: 'exception', label: t(`admin:organisations.exceptions.${exception}`), onRemove: () => { setException(null); resetPage(); } }] : []),
  ];
  const clearFilters = () => { setQuery(''); setStatus('all'); setException(null); resetPage(); };

  // SR1-1.6 / ENG-17: permanent purge of the selected organisations.
  function toggle(id: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }
  // REV-W7-F4: the selection only ever holds rows on screen. Changing the
  // search, a filter, the sort or the page clears it, so a purge can never
  // include an organisation the admin no longer sees.
  useEffect(() => {
    setSelected((prev) => (prev.size ? new Set() : prev));
    setPurgeError(null);
  }, [needle, status, exception, sort, currentPage]);
  const selectedRows = all.filter((o) => selected.has(o.id));
  // Only a disabled or soft-deleted organisation can be purged: an active one
  // is deactivated (or soft-deleted) first.
  const activeSelected = selectedRows.filter((o) => statusOf(o) === 'active');
  const purgeBlocked = activeSelected.length > 0;
  const purgePhrase = purgeConfirmPhrase(selectedRows.map(confirmNameOf));
  const { notify: notifyLifecycleError } = useLifecycleError();
  const runPurge = async () => {
    setPurgeError(null);
    try {
      const { deleted, numbersLeft } = await purgeOrganisations(selectedRows.map((o) => o.id), purgePhrase);
      setPurgeOpen(false);
      setSelected(new Set());
      toast.success(t('admin:organisations.lifecycle.purged', { count: deleted }));
      // REV-W7-F5: the purge never releases phone numbers at the carrier.
      if (numbersLeft > 0) {
        toast.warning(t('admin:organisations.lifecycle.numbersLeft', { count: numbersLeft }), {
          duration: 15_000,
          action: { label: t('admin:organisations.lifecycle.numbersLeftAction'), onClick: () => navigate('/TwilioConfig?tab=numbers') },
        });
      }
    } catch (err) {
      // A failed bulk action keeps the selection and offers Réessayer.
      setPurgeError(err);
      notifyLifecycleError(err);
      setPurgeOpen(false);
      setPurgeAttempt((n) => n + 1);
      throw err;
    } finally {
      void qc.invalidateQueries({ queryKey: ['admin', 'organisations'] });
    }
  };

  const exceptionCounts = useMemo(
    () => Object.fromEntries(EXCEPTIONS.map((k) => [k, all.filter((o) => isException(o, k)).length])) as Record<Exception, number>,
    [all],
  );

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5">
      <PageHeader
        as="h1"
        title={t('admin:organisations.title')}
        subtitle={t('admin:organisations.subtitle')}
        actions={
          canCreateOrganisation ? (
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="size-4" />
                  {t('admin:organisations.new')}
                </Button>
              </DialogTrigger>
              <CreateOrganisationDialog onSuccess={() => setIsCreateOpen(false)} />
            </Dialog>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); resetPage(); }}
          placeholder={t('admin:organisations.search')}
          aria-label={t('admin:organisations.search')}
          className="max-w-sm"
        />
        <Select value={status} onValueChange={(v) => { setStatus(v as StatusFilter); resetPage(); }}>
          <SelectTrigger className="w-44" aria-label={t('admin:organisations.status.label')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(['all', 'active', 'inactive', 'deleted'] as const).map((v) => (
              <SelectItem key={v} value={v}>{t(`admin:organisations.status.${v}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedRows.length > 0 && (
        <div role="region" aria-label={t('admin:organisations.lifecycle.selection', { count: selectedRows.length })} className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <span className="font-medium">{t('admin:organisations.lifecycle.selection', { count: selectedRows.length })}</span>
          <Button size="sm" variant="ghost" className="min-h-11" onClick={() => { setSelected(new Set()); setPurgeError(null); }}>
            {t('admin:organisations.lifecycle.clearSelection')}
          </Button>
          <Button
            size="sm" variant="destructive" className="min-h-11 gap-2"
            disabled={purgeBlocked}
            aria-describedby={purgeBlocked ? 'purge-blocked-reason' : undefined}
            onClick={() => setPurgeOpen(true)}
          >
            <Trash2 className="size-4" aria-hidden /> {t('admin:organisations.lifecycle.purge')}
          </Button>
          {purgeBlocked && (
            <span id="purge-blocked-reason" className="text-muted-foreground">
              {t('admin:organisations.lifecycle.purgeActiveBlocked', { count: activeSelected.length, names: activeSelected.map((o) => o.company_name).join(', ') })}
            </span>
          )}
          {purgeError ? (
            <span className="flex flex-wrap items-center gap-2">
              <ErrorLine error={purgeError} />
              <Button size="sm" variant="outline" className="min-h-11" disabled={purgeBlocked} onClick={() => setPurgeOpen(true)}>{t('common:actions.retry')}</Button>
            </span>
          ) : null}
        </div>
      )}

      <ConfirmDialog
        key={purgeAttempt}
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        title={t('admin:organisations.lifecycle.purgeTitle', { count: selectedRows.length })}
        description={
          <span className="block space-y-2">
            <span className="block">{t('admin:organisations.lifecycle.purgeImpact')}</span>
            <span className="block font-medium">{t('admin:organisations.lifecycle.purgeList')}</span>
            <span role="list" className="block max-h-40 overflow-y-auto">
              {selectedRows.map((o) => <span role="listitem" key={o.id} className="block">{confirmNameOf(o)}</span>)}
            </span>
          </span>
        }
        confirmLabel={t('admin:organisations.lifecycle.purge')}
        typedName={purgePhrase}
        onConfirm={runPurge}
      />

      <DataTable<OrganisationRow>
        caption={t('admin:organisations.title')}
        storageKey="admin-organisations-w6"
        columns={columns}
        rows={pageRows}
        getRowId={(org) => org.id}
        status={isLoading ? 'loading' : error ? 'error' : 'ready'}
        // DES-16: the readable message first, the code beside it for support.
        error={error ? { message: toUserMessage(error), code: error instanceof ApiErrorException ? error.code : undefined, onRetry: () => void refetch() } : undefined}
        sort={sort}
        onSortChange={(s) => { setSort(s); resetPage(); }}
        filters={filters}
        onClearFilters={clearFilters}
        empty={{
          title: t('admin:organisations.empty'),
          action: canCreateOrganisation ? <Button size="sm" onClick={() => setIsCreateOpen(true)}>{t('admin:organisations.new')}</Button> : undefined,
        }}
        pagination={rows.length > PAGE_SIZE ? { page: currentPage, pageSize: PAGE_SIZE, total: rows.length, onPageChange: setPage } : undefined}
        partial={data && (data.invalid > 0 || data.total > data.organisations.length + data.invalid) ? {
          shown: data.organisations.length,
          total: Math.max(data.total, data.organisations.length + data.invalid),
          reason: data.invalid > 0 ? t('admin:organisations.unreadable', { count: data.invalid }) : t('admin:organisations.capped'),
        } : undefined}
      />

      {data && (
        <p className="text-sm text-muted-foreground">
          {/* QA-W7-7: the count follows the filters; the total only when it differs. */}
          {rows.length === data.total
            ? t('admin:organisations.total', { count: data.total })
            : t('admin:organisations.shownOfTotal', { count: rows.length, total: data.total })}
        </p>
      )}

      {/* DES-11: the exceptions come after the table, as shortcuts into it. */}
      {data && (
        <section aria-labelledby="org-exceptions" className="rounded-lg border p-4 space-y-3">
          <h2 id="org-exceptions" className="flex items-center gap-2 text-sm font-semibold">
            <AlertTriangle className="size-4 text-amber-600" aria-hidden /> {t('admin:organisations.exceptions.title')}
          </h2>
          {EXCEPTIONS.every((k) => exceptionCounts[k] === 0) ? (
            <p className="text-sm text-muted-foreground">{t('admin:organisations.exceptions.none')}</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {EXCEPTIONS.filter((k) => exceptionCounts[k] > 0).map((k) => (
                <li key={k}>
                  <Button
                    variant={exception === k ? 'default' : 'outline'}
                    size="sm"
                    className="min-h-11"
                    aria-pressed={exception === k}
                    onClick={() => { setException(exception === k ? null : k); resetPage(); }}
                  >
                    {t(`admin:organisations.exceptions.${k}`)}
                    <span className="ml-2 tabular-nums">{exceptionCounts[k]}</span>
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">{t('admin:organisations.exceptions.hint')}</p>
        </section>
      )}
    </div>
  );
}

/** W6: the first admin is invited by email (welcome link to choose a password). */
function CreateOrganisationDialog({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useTranslation(['admin']);
  const create = useCreateOrganisation();
  const [form, setForm] = useState({
    email: '',
    companyName: '',
    contactName: '',
    adminEmail: '',
    adminFullName: '',
  });

  type Key = keyof typeof form;
  // QA-W6-6: inline, translated errors (noValidate), like the sign-in page.
  const [errors, setErrors] = useState<Partial<Record<Key, string>>>({});
  const update = (key: Key) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((s) => ({ ...s, [key]: e.target.value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };
  const ORDER: Key[] = ['companyName', 'email', 'contactName', 'adminEmail', 'adminFullName'];
  const INPUT_ID: Record<Key, string> = { companyName: 'org-company', email: 'org-email', contactName: 'org-contact', adminEmail: 'admin-email', adminFullName: 'admin-name' };
  const validate = (): Partial<Record<Key, string>> => {
    const out: Partial<Record<Key, string>> = {};
    for (const key of ORDER) {
      if (key === 'email' || key === 'adminEmail') out[key] = emailFieldError(form[key]);
      else if (!form[key].trim()) out[key] = 'admin:organisations.createDialog.required';
    }
    return out;
  };
  const field = (key: Key, label: string, type: 'text' | 'email' = 'text') => {
    const id = INPUT_ID[key];
    const err = errors[key];
    return (
      <div className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <Input
          id={id}
          type={type}
          required
          value={form[key]}
          onChange={update(key)}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${id}-error` : undefined}
        />
        {err && <p id={`${id}-error`} className="text-xs text-destructive">{t(err)}</p>}
      </div>
    );
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    const first = ORDER.find((k) => found[k]);
    if (first) {
      document.getElementById(INPUT_ID[first])?.focus();
      return;
    }
    try {
      const created = await create.mutateAsync({
        email: form.email,
        companyName: form.companyName,
        contactName: form.contactName,
        adminUser: { email: form.adminEmail, fullName: form.adminFullName },
      });
      const names = { company: created.organisation.company_name, email: created.admin.email };
      // REV-W6-S4: the server says whether the welcome email left.
      if (created.invited && created.email_sent === false) {
        toast.warning(t('admin:organisations.createDialog.invitedNotSent', names));
      } else {
        toast.success(t('admin:organisations.createDialog.invited', names));
      }
      onSuccess();
    } catch (err) {
      if (err instanceof ApiErrorException && err.code === 'EMAIL_UNAVAILABLE') {
        toast.error(t('admin:organisations.createDialog.emailUnavailable'));
        return;
      }
      showErrorToast(err);
    }
  }

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>{t('admin:organisations.createDialog.title')}</DialogTitle>
        <DialogDescription>
          {t('admin:organisations.createDialog.description')}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit} noValidate className="space-y-4 py-2">
        {field('companyName', t('admin:organisations.createDialog.companyName'))}
        <div className="grid gap-3 sm:grid-cols-2">
          {field('email', t('admin:organisations.createDialog.orgEmail'), 'email')}
          {field('contactName', t('admin:organisations.createDialog.contactName'))}
        </div>
        <div className="border-t pt-4 space-y-4">
          <p className="text-sm font-medium">{t('admin:organisations.createDialog.firstAdmin')}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {field('adminEmail', t('admin:organisations.createDialog.adminEmail'), 'email')}
            {field('adminFullName', t('admin:organisations.createDialog.adminFullName'))}
          </div>
          <p className="text-xs text-muted-foreground">{t('admin:organisations.createDialog.inviteHint')}</p>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? t('admin:organisations.createDialog.creating') : t('admin:organisations.createDialog.create')}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
